/**
 * Voluntary Life & AD&D Benefit Definitions
 *
 * Uses builder functions for type/amount pairs.
 * Enum values from @platform/openapi-types passed via typeEnumValues.
 */

import {
  addNotDisclosed,
  BaseBenefitPlanPolicyPeriod,
  DefinitionOfEarningsEnum,
  extendEnumWithNotDisclosed,
  extendEnumWithNotOffered,
  RateGuaranteeTypeEnum,
  RfpInclusionStatusEnum,
  RfpPortabilityType,
  RfpVoluntaryLifeAdndTiedToLifeEnum,
  RfpVoluntaryLifeAgeReductionScheduleTypeEnum,
  RfpVoluntaryLifeAmountNotToExceedTypeEnum,
  RfpVoluntaryLifeBenefitMaximumTypeEnum,
  RfpVoluntaryLifeEmployeeBenefitTypeEnum,
  RfpVoluntaryLifeGuaranteeIssueTypeEnum,
  RfpVoluntaryLifeSpouseChildBenefitTypeEnum,
} from "@platform/openapi-types";

import { mpOnlyRow } from "../shared/definitions/mpOnlyRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import {
  createEnumFormatter,
  createValueFormatter,
} from "../../components/PlanEditor/utilities/enumFormatters";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import {
  definitionOfEarningsLabels,
  DOLLAR_AMOUNT_LABEL,
  SALARY_MULTIPLIER_LABEL,
} from "../shared/definitions";
import {
  buildAgeReductionScheduleBenefitPair,
  buildCommissionBenefits,
  buildTypeAmountBenefitPair,
  buildTypeMultipleAmountsBenefit,
  replaceMultipleOptionsWithFrontendTypes,
} from "../shared/definitions/builders";

import * as cellSpecs from "./cellSpecs";
import {
  spouseAmountNotToExceedLabels,
  spouseChildBenefitDescriptionLabels,
} from "./customLabels";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

// Spouse and child benefit descriptions store a list of amounts as the API's
// `multipleOptions` type; the frontend names that case `multipleDollarAmounts`
// so the editor knows to collect a list. Their enum has no salary-multiplier
// type, so the multiplier counterpart the employee offers does not apply.
const SPOUSE_CHILD_BENEFIT_TYPE_VALUES = addNotDisclosed([
  ...Object.values(RfpVoluntaryLifeSpouseChildBenefitTypeEnum).filter(
    (value) =>
      value !== RfpVoluntaryLifeSpouseChildBenefitTypeEnum.multipleOptions,
  ),
  "multipleDollarAmounts",
]);

export const benefits: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: sharedCellSpecs.currentCarrierName,
    benefitPlanLevel: true,
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  eligibilityHours: {
    mpOnly: true,
    label: "Eligibility Hours",
    mpConfigKey: "eligibility_hours",
    description: "Minimum number of hours worked per week to be eligible",
    cell: sharedCellSpecs.eligibilityHoursDropdown,
    networkDependent: false,
  },
  // The employee/spouse age reduction schedule *types* (CD-2071) are
  // distinct from the existing v5 `ageReductionSchedule` benefit pair below.
  // Their read display and editor come from the Quotes formatting layer
  // (cellFormatOverride / cellEditorOverride), same as the schedule detail
  // rows further down (`employeeAgeReductionSchedules`/
  // `spouseAgeReductionSchedules`).
  ageReductionsEe: {
    mpOnly: true,
    label: "Age Reductions - EE",
    mpConfigKey: "age_reductions_ee",
    description: "Employee age reduction schedule type",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotOffered(
          RfpVoluntaryLifeAgeReductionScheduleTypeEnum,
        ),
      },
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotOffered(
          RfpVoluntaryLifeAgeReductionScheduleTypeEnum,
        ),
      },
    },
    networkDependent: false,
  },
  ageReductionsSp: {
    mpOnly: true,
    label: "Age Reductions - SP",
    mpConfigKey: "age_reductions_sp",
    description: "Spouse age reduction schedule type",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotOffered(
          RfpVoluntaryLifeAgeReductionScheduleTypeEnum,
        ),
      },
      validation: {
        ...sharedCellSpecs.enumDropdown.validation,
        allowedValues: extendEnumWithNotOffered(
          RfpVoluntaryLifeAgeReductionScheduleTypeEnum,
        ),
      },
    },
    networkDependent: false,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "benefitDescriptionEmployee",
    typeLabel: "Benefit Description - Employee",
    typeDescription: "Describes the benefit configuration available",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: addNotDisclosed(
      replaceMultipleOptionsWithFrontendTypes(
        Object.values(RfpVoluntaryLifeEmployeeBenefitTypeEnum),
        false, // salaryMultiplier already in enum
      ),
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    amountLabel: "Amount",
    amountDescription: "Benefit description amount",
    amountDefaultValue: 1,
    amountCellVariants: {
      options: {
        [RfpVoluntaryLifeEmployeeBenefitTypeEnum.salaryMultiplier]:
          sharedCellSpecs.salaryMultiplier,
        [RfpVoluntaryLifeEmployeeBenefitTypeEnum.multipleOptions]:
          sharedCellSpecs.multipleAmounts,
        multipleDollarAmounts: sharedCellSpecs.multipleDollarAmountsPopover,
        multipleSalaryMultipliers:
          sharedCellSpecs.multipleSalaryMultipliersPopover,
      },
      default: sharedCellSpecs.dollarAmount,
    },
    isRequired: true,
    isValueRequired: true,
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "benefitMaximumEmployee",
    typeLabel: "Benefit Maximum - Employee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpVoluntaryLifeBenefitMaximumTypeEnum,
    ),
    typeDescription:
      "Indicates the maximum amount of benefit the employee can elect",
    amountFields: [
      {
        suffix: "DollarAmount",
        label: DOLLAR_AMOUNT_LABEL,
        cell: sharedCellSpecs.dollarAmount,
        description: "Maximum benefit dollar amount",
        disabledValues: addNotDisclosed([
          RfpVoluntaryLifeBenefitMaximumTypeEnum.salaryMultiplier,
        ]),
      },
      {
        suffix: "SalaryMultiplier",
        label: SALARY_MULTIPLIER_LABEL,
        cell: sharedCellSpecs.salaryMultiplier,
        description: "Maximum benefit salary multiplier",
        defaultValue: 1,
        disabledValues: addNotDisclosed([
          RfpVoluntaryLifeBenefitMaximumTypeEnum.dollarAmount,
        ]),
      },
    ],
    isRequired: true,
    isValueRequired: true,
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "guaranteeIssueEmployee",
    typeLabel: "Guarantee Issue - Employee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpVoluntaryLifeGuaranteeIssueTypeEnum,
    ),
    typeDescription:
      "Indicates the amount of Life Insurance volume available without having to provide Evidence of Insurability",
    amountFields: [
      {
        suffix: "SalaryMultiplier",
        label: SALARY_MULTIPLIER_LABEL,
        cell: sharedCellSpecs.salaryMultiplier,
        description: "Employee guarantee issue salary multiplier",
        defaultValue: 1,
        disabledValues: addNotDisclosed([
          "notIncluded",
          "ageBasedAmounts",
          "allGuaranteeIssue",
          "dollarAmount",
        ]),
      },
      {
        suffix: "DollarAmount",
        label: DOLLAR_AMOUNT_LABEL,
        cell: sharedCellSpecs.dollarAmount,
        description: "Employee guarantee issue dollar amount",
        defaultValue: 1,
        disabledValues: addNotDisclosed([
          "notIncluded",
          "ageBasedAmounts",
          "allGuaranteeIssue",
        ]),
      },
      {
        suffix: "AdditionalAmount",
        label: "Additional Amount",
        cell: sharedCellSpecs.dollarAmount,
        description: "Employee guarantee issue additional amount",
        defaultValue: 1,
        disabledValues: addNotDisclosed([
          "notIncluded",
          "ageBasedAmounts",
          "allGuaranteeIssue",
          "dollarAmount",
          "salaryMultiplierWithCap",
        ]),
      },
    ],
    isRequired: true,
    isValueRequired: true,
  }),
  guaranteeIssueEmployeeAgesDetails: {
    label: "Age-Based Details",
    description: "Configure guarantee issue amounts by age band",
    cell: cellSpecs.guaranteeIssueAgesDetails,
    nested: true,
    networkDependent: false,
    editableWhen: {
      dependsOn: "guaranteeIssueEmployeeType",
      disabledValues: addNotDisclosed([
        "dollarAmount",
        "notIncluded",
        "allGuaranteeIssue",
      ]),
    },
  },
  spouseBenefitsIncluded: {
    label: "Spouse Benefits Included",
    description: "Whether spouse benefits are included",
    cell: sharedCellSpecs.benefitsIncluded,
    networkDependent: false,
    defaultValue: "yes",
    refreshesCells: [
      "benefitDescriptionSpouseType",
      "benefitMaximumSpouseType",
      "guaranteeIssueSpouseType",
      "amountNotToExceedSpouseType",
    ],
  },
  benefitDescriptionSpouseType: {
    label: "Benefit Description - Spouse",
    description: "Describes the benefit configuration available for spouse",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: SPOUSE_CHILD_BENEFIT_TYPE_VALUES,
        formatValue: createEnumFormatter(spouseChildBenefitDescriptionLabels),
      },
      valueFormatter: createValueFormatter(spouseChildBenefitDescriptionLabels),
    },
    networkDependent: false,
    editableWhen: {
      dependsOn: "spouseBenefitsIncluded",
      disabledValues: ["no"],
      resetValue: "notIncluded",
    },
    isRequired: true,
  },
  benefitDescriptionSpouseAmount: {
    label: "Amount",
    description: "Spouse benefit description amount",
    cell: sharedCellSpecs.dollarAmount,
    nested: true,
    networkDependent: false,
    defaultValue: 0,
    editableWhen: {
      dependsOn: "benefitDescriptionSpouseType",
      disabledValues: addNotDisclosed(["notIncluded"]),
    },
    cellVariants: {
      dependsOn: "benefitDescriptionSpouseType",
      options: {
        [RfpVoluntaryLifeSpouseChildBenefitTypeEnum.percentageOfEmployeeElection]:
          sharedCellSpecs.percentageAmount,
        [RfpVoluntaryLifeSpouseChildBenefitTypeEnum.multipleOptions]:
          sharedCellSpecs.multipleDollarAmountsPopover,
        multipleDollarAmounts: sharedCellSpecs.multipleDollarAmountsPopover,
      },
      default: sharedCellSpecs.dollarAmount,
    },
    isRequired: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "benefitMaximumSpouse",
    typeLabel: "Benefit Maximum - Spouse",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: addNotDisclosed(["dollarAmount", "notIncluded"]),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Indicates the maximum amount of benefit for spouse",
    amountDescription: "Spouse maximum benefit amount",
    disabledValues: addNotDisclosed(["notIncluded"]),
    typeEditableWhen: {
      dependsOn: "spouseBenefitsIncluded",
      disabledValues: ["no"],
      resetValue: "notIncluded",
    },
    isRequired: true,
    isValueRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "amountNotToExceedSpouse",
    typeLabel: "Amount Not to Exceed - Spouse",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpVoluntaryLifeAmountNotToExceedTypeEnum,
    ),
    typeCustomLabels: spouseAmountNotToExceedLabels,
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Indicates limitation on Spouse benefit",
    amountDescription: "Spouse amount not to exceed percentage",
    amountDefaultValue: 1,
    disabledValues: addNotDisclosed(["notIncluded", "notApplicable"]),
    typeEditableWhen: {
      dependsOn: "spouseBenefitsIncluded",
      disabledValues: ["no"],
      resetValue: "notIncluded",
    },
    isRequired: true,
    isValueRequired: true,
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "guaranteeIssueSpouse",
    typeLabel: "Guarantee Issue - Spouse",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpVoluntaryLifeGuaranteeIssueTypeEnum,
    ),
    typeDescription:
      "Indicates the amount of Life Insurance volume available for spouse without EOI",
    amountFields: [
      {
        suffix: "SalaryMultiplier",
        label: SALARY_MULTIPLIER_LABEL,
        cell: sharedCellSpecs.salaryMultiplier,
        description: "Spouse guarantee issue salary multiplier",
        defaultValue: 1,
        disabledValues: addNotDisclosed([
          "notIncluded",
          "ageBasedAmounts",
          "allGuaranteeIssue",
          "dollarAmount",
        ]),
      },
      {
        suffix: "DollarAmount",
        label: DOLLAR_AMOUNT_LABEL,
        cell: sharedCellSpecs.dollarAmount,
        description: "Spouse guarantee issue dollar amount",
        defaultValue: 1,
        disabledValues: addNotDisclosed([
          "notIncluded",
          "ageBasedAmounts",
          "allGuaranteeIssue",
        ]),
      },
      {
        suffix: "AdditionalAmount",
        label: "Additional Amount",
        cell: sharedCellSpecs.dollarAmount,
        description: "Spouse guarantee issue additional amount",
        defaultValue: 1,
        disabledValues: addNotDisclosed([
          "notIncluded",
          "ageBasedAmounts",
          "allGuaranteeIssue",
          "dollarAmount",
          "salaryMultiplierWithCap",
        ]),
      },
    ],
    typeEditableWhen: {
      dependsOn: "spouseBenefitsIncluded",
      disabledValues: ["no"],
      resetValue: "notIncluded",
    },
    isRequired: true,
    isValueRequired: true,
  }),
  guaranteeIssueSpouseAgesDetails: {
    label: "Age-Based Details",
    description: "Configure spouse guarantee issue amounts by age band",
    cell: cellSpecs.guaranteeIssueAgesDetails,
    nested: true,
    networkDependent: false,
    editableWhen: {
      dependsOn: "guaranteeIssueSpouseType",
      disabledValues: addNotDisclosed([
        "dollarAmount",
        "notIncluded",
        "allGuaranteeIssue",
      ]),
    },
  },
  childBenefitsIncluded: {
    label: "Child Benefits Included",
    description: "Whether child benefits are included",
    cell: sharedCellSpecs.benefitsIncluded,
    networkDependent: false,
    defaultValue: "yes",
    refreshesCells: [
      "benefitDescriptionChildDependentAgesBirthTo14DaysType",
      "benefitDescriptionChildDependentAges15DaysTo6MonthsType",
      "benefitDescriptionChildDependentAges6MonthsToLimitingAgeType",
      "benefitMaximumChildType",
      "amountNotToExceedChildType",
    ],
  },
  benefitDescriptionChildDependentAgesBirthTo14DaysType: {
    label: "Benefit Description - Child (Birth to 14 days)",
    description: "Describes the benefit configuration for newborns",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: SPOUSE_CHILD_BENEFIT_TYPE_VALUES,
        formatValue: createEnumFormatter(spouseChildBenefitDescriptionLabels),
      },
      valueFormatter: createValueFormatter(spouseChildBenefitDescriptionLabels),
    },
    networkDependent: false,
    editableWhen: {
      dependsOn: "childBenefitsIncluded",
      disabledValues: ["no"],
      resetValue: "notIncluded",
    },
    isRequired: true,
  },
  benefitDescriptionChildDependentAgesBirthTo14DaysAmount: {
    label: "Amount",
    description: "Child benefit amount for birth to 14 days",
    cell: sharedCellSpecs.dollarAmount,
    nested: true,
    networkDependent: false,
    defaultValue: 0,
    editableWhen: {
      dependsOn: "benefitDescriptionChildDependentAgesBirthTo14DaysType",
      disabledValues: addNotDisclosed(["notIncluded"]),
    },
    cellVariants: {
      dependsOn: "benefitDescriptionChildDependentAgesBirthTo14DaysType",
      options: {
        [RfpVoluntaryLifeSpouseChildBenefitTypeEnum.percentageOfEmployeeElection]:
          sharedCellSpecs.percentageAmount,
        [RfpVoluntaryLifeSpouseChildBenefitTypeEnum.multipleOptions]:
          sharedCellSpecs.multipleDollarAmountsPopover,
        multipleDollarAmounts: sharedCellSpecs.multipleDollarAmountsPopover,
      },
      default: sharedCellSpecs.dollarAmount,
    },
    isRequired: true,
  },
  benefitDescriptionChildDependentAges15DaysTo6MonthsType: {
    label: "Benefit Description - Child (15 days to 6 months)",
    description: "Describes the benefit configuration for infants",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: SPOUSE_CHILD_BENEFIT_TYPE_VALUES,
        formatValue: createEnumFormatter(spouseChildBenefitDescriptionLabels),
      },
      valueFormatter: createValueFormatter(spouseChildBenefitDescriptionLabels),
    },
    networkDependent: false,
    editableWhen: {
      dependsOn: "childBenefitsIncluded",
      disabledValues: ["no"],
      resetValue: "notIncluded",
    },
    isRequired: true,
  },
  benefitDescriptionChildDependentAges15DaysTo6MonthsAmount: {
    label: "Amount",
    description: "Child benefit amount for 15 days to 6 months",
    cell: sharedCellSpecs.dollarAmount,
    nested: true,
    networkDependent: false,
    defaultValue: 0,
    editableWhen: {
      dependsOn: "benefitDescriptionChildDependentAges15DaysTo6MonthsType",
      disabledValues: addNotDisclosed(["notIncluded"]),
    },
    cellVariants: {
      dependsOn: "benefitDescriptionChildDependentAges15DaysTo6MonthsType",
      options: {
        [RfpVoluntaryLifeSpouseChildBenefitTypeEnum.percentageOfEmployeeElection]:
          sharedCellSpecs.percentageAmount,
        [RfpVoluntaryLifeSpouseChildBenefitTypeEnum.multipleOptions]:
          sharedCellSpecs.multipleDollarAmountsPopover,
        multipleDollarAmounts: sharedCellSpecs.multipleDollarAmountsPopover,
      },
      default: sharedCellSpecs.dollarAmount,
    },
    isRequired: true,
  },
  benefitDescriptionChildDependentAges6MonthsToLimitingAgeType: {
    label: "Benefit Description - Child (6 months to limiting age)",
    description: "Describes the benefit configuration for children",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: SPOUSE_CHILD_BENEFIT_TYPE_VALUES,
        formatValue: createEnumFormatter(spouseChildBenefitDescriptionLabels),
      },
      valueFormatter: createValueFormatter(spouseChildBenefitDescriptionLabels),
    },
    networkDependent: false,
    editableWhen: {
      dependsOn: "childBenefitsIncluded",
      disabledValues: ["no"],
      resetValue: "notIncluded",
    },
    isRequired: true,
  },
  benefitDescriptionChildDependentAges6MonthsToLimitingAgeAmount: {
    label: "Amount",
    description: "Child benefit amount for 6 months to limiting age",
    cell: sharedCellSpecs.dollarAmount,
    nested: true,
    networkDependent: false,
    defaultValue: 0,
    editableWhen: {
      dependsOn: "benefitDescriptionChildDependentAges6MonthsToLimitingAgeType",
      disabledValues: addNotDisclosed(["notIncluded"]),
    },
    cellVariants: {
      dependsOn: "benefitDescriptionChildDependentAges6MonthsToLimitingAgeType",
      options: {
        [RfpVoluntaryLifeSpouseChildBenefitTypeEnum.percentageOfEmployeeElection]:
          sharedCellSpecs.percentageAmount,
        [RfpVoluntaryLifeSpouseChildBenefitTypeEnum.multipleOptions]:
          sharedCellSpecs.multipleDollarAmountsPopover,
        multipleDollarAmounts: sharedCellSpecs.multipleDollarAmountsPopover,
      },
      default: sharedCellSpecs.dollarAmount,
    },
    isRequired: true,
  },
  childLimitingAge: {
    label: "Limiting Age",
    description:
      "The maximum age (in years) for child dependent coverage in the 6 months to limiting age range",
    cell: {
      ...sharedCellSpecs.numberField,
      cellEditorParams: {
        ...sharedCellSpecs.numberField.cellEditorParams,
        min: 1,
        max: 100,
      },
      validation: {
        minimum: 1,
        maximum: 100,
      },
    },
    nested: true,
    networkDependent: false,
    editableWhen: {
      dependsOn: "benefitDescriptionChildDependentAges6MonthsToLimitingAgeType",
      disabledValues: addNotDisclosed(["notIncluded"]),
    },
  },
  // Present renders the three fixed child benefit-description bands above as a
  // single flexible, from-scratch field (CD-1976); Build shows the same
  // flexible field only when the btx-ch-benefits-field flag is on. This row is
  // not mpOnly - both surfaces can render it - but btx-plan-designer's
  // hideProposalOnlyRows swaps it in for, and hides, the three fixed rows
  // above (plus childLimitingAge) whenever the flag is on. On Present the read
  // display and editor still come from the Quotes formatting layer
  // (cellFormatOverride / cellEditorOverride), which take precedence over this
  // row's own cell spec.
  childBenefitAmountsByAge: {
    label: "Benefit Description & Ages - CH",
    description: "Child benefit amounts by age band",
    cell: cellSpecs.childBenefitAmountsByAge,
    networkDependent: false,
    editableWhen: {
      dependsOn: "childBenefitsIncluded",
      disabledValues: ["no"],
    },
  },
  ...buildTypeAmountBenefitPair({
    prefix: "studentStatusStudentLimitingAge",
    typeLabel: "Student Limiting Age",
    typeCell: sharedCellSpecs.enumDropdown,
    amountLabel: "Age",
    typeEnumValues: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Indicates if child benefits are offered beyond the child limiting age for students",
    amountDescription: "Maximum age a child can be for coverage as student",
    amountDefaultValue: 1,
    disabledValues: addNotDisclosed(["notIncluded"]),
    isRequired: true,
    isValueRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "benefitMaximumChild",
    typeLabel: "Benefit Maximum - Child",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: addNotDisclosed(["dollarAmount", "notIncluded"]),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Indicates the maximum amount of benefit for child",
    amountDescription: "Child maximum benefit amount",
    disabledValues: addNotDisclosed(["notIncluded"]),
    typeEditableWhen: {
      dependsOn: "childBenefitsIncluded",
      disabledValues: ["no"],
      resetValue: "notIncluded",
    },
    isRequired: true,
    isValueRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "amountNotToExceedChild",
    typeLabel: "Amount Not to Exceed - Child",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpVoluntaryLifeAmountNotToExceedTypeEnum,
    ),
    typeCustomLabels: spouseAmountNotToExceedLabels,
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Indicates limitation on Child benefit",
    amountDescription: "Child amount not to exceed percentage",
    amountDefaultValue: 1,
    disabledValues: addNotDisclosed(["notIncluded", "notApplicable"]),
    typeEditableWhen: {
      dependsOn: "childBenefitsIncluded",
      disabledValues: ["no"],
      resetValue: "notIncluded",
    },
    isRequired: true,
    isValueRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "definitionOfEarnings",
    mpConfigKey: "definition_of_earnings",
    typeLabel: "Definition of Earnings",
    typeDescription: "Indicates the earnings covered by the plan",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(DefinitionOfEarningsEnum),
    typeCustomLabels: definitionOfEarningsLabels,
    amountLabel: "Average Months",
    amountCell: sharedCellSpecs.numberField,
    amountDescription: "The time period covered when considering earnings",
    // Blank, not 1: average months is optional, and 0 is a real value.
    amountDefaultValue: null,
    valueSuffix: "AverageMonths",
    disabledValues: addNotDisclosed([
      DefinitionOfEarningsEnum.notApplicable,
      DefinitionOfEarningsEnum.notIncluded,
      DefinitionOfEarningsEnum.salary,
      DefinitionOfEarningsEnum.priorYearW2,
      DefinitionOfEarningsEnum.k1,
    ]),
    isRequired: true,
    isValueRequired: true,
  }),
  adndIncluded: {
    label: "AD&D Included",
    description: "Indicates if AD&D benefit is included with Life benefit",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
    isRequired: true,
    refreshesCells: [
      {
        cell: "adndTiedToLifeElection",
        values: [RfpInclusionStatusEnum.notIncluded],
      },
    ],
  },
  adndTiedToLifeElection: {
    label: "AD&D Tied to Life",
    description:
      "Indicates if AD&D benefit amount must match vol life benefit amount",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpVoluntaryLifeAdndTiedToLifeEnum),
      },
    },
    networkDependent: false,
    editableWhen: {
      dependsOn: "adndIncluded",
      disabledValues: [RfpInclusionStatusEnum.notIncluded],
      resetValue: RfpVoluntaryLifeAdndTiedToLifeEnum.untied,
      showValueWhenDisabled: true,
    },
    isRequired: true,
  },
  ...buildAgeReductionScheduleBenefitPair({
    typeLabel: "Age Reductions",
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpVoluntaryLifeAgeReductionScheduleTypeEnum,
    ),
    isRequired: true,
    isValueRequired: true,
  }),
  portability: {
    label: "Portability (with or without EOI)",
    mpConfigKey: "portability",
    description:
      "Indicates if policy holder can continue policy after separation of employment and whether Evidence of Insurability would be required",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpPortabilityType),
      },
    },
    networkDependent: false,
    isRequired: true,
  },
  conversion: {
    label: "Conversion",
    mpConfigKey: "conversion",
    description:
      "Indicates if the employee will be allowed to convert the life policy to a different form of life policy",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
    isRequired: true,
  },
  acceleratedDeathBenefit: {
    label: "Accelerated Death Benefit",
    mpConfigKey: "accelerated_death_benefit",
    description:
      "Indicates if the plan will allow for any amount of life insurance benefit to be paid out in advance of death",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
    isRequired: true,
  },
  suicideExclusion: {
    mpOnly: true,
    label: "Suicide Exclusion",
    mpConfigKey: "suicide_exclusion",
    description: "Whether death by suicide is excluded from the benefit",
    cell: sharedCellSpecs.suicideExclusionDropdown,
    networkDependent: false,
  },
  waiverOfPremium: {
    label: "Waiver of Premium",
    mpConfigKey: "waiver_of_premium",
    description:
      "Indicates if premium payments will be waived in the case of a critical illness or serious injury",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
    isRequired: true,
  },
  // Eligibility/Waiting Period/Duration are "Integer > 0" per the spreadsheet
  // (an age or a month count, e.g. "Age 60" / "9 months"), not an enum - text
  // is the correct cell here, not a missing dropdown (CD-2325).
  waiverOfPremiumEligibility: {
    mpOnly: true,
    label: "Waiver of Premium - Eligibility",
    mpConfigKey: "waiver_of_premium_eligibility",
    description: "Eligibility requirements for the waiver of premium benefit",
    // An age of 0 isn't a real eligibility age, so this is a positive
    // integer (min 1), not the generic numberField's min 0.
    cell: {
      ...sharedCellSpecs.numberField,
      prefix: "Age",
      cellEditorParams: {
        ...sharedCellSpecs.numberField.cellEditorParams,
        min: 1,
      },
      validation: { ...sharedCellSpecs.numberField.validation, minimum: 1 },
    },
    networkDependent: false,
  },
  waiverOfPremiumWaitingPeriod: {
    mpOnly: true,
    label: "Waiver of Premium Waiting Period",
    mpConfigKey: "waiver_of_premium_waiting_period",
    description: "Waiting period before the waiver of premium benefit applies",
    cell: { ...sharedCellSpecs.numberField, suffix: "months" },
    networkDependent: false,
  },
  waiverOfPremiumDuration: {
    mpOnly: true,
    label: "Waiver of Premium - Duration",
    mpConfigKey: "waiver_of_premium_duration",
    description: "Duration the waiver of premium benefit remains in effect",
    // An age of 0 isn't a real duration age, so this is a positive integer
    // (min 1), not the generic numberField's min 0.
    cell: {
      ...sharedCellSpecs.numberField,
      prefix: "Age",
      cellEditorParams: {
        ...sharedCellSpecs.numberField.cellEditorParams,
        min: 1,
      },
      validation: { ...sharedCellSpecs.numberField.validation, minimum: 1 },
    },
    networkDependent: false,
  },
  airBag: {
    mpOnly: true,
    label: "Air Bag",
    mpConfigKey: "air_bag",
    description: "AD&D air bag benefit provision",
    cell: sharedCellSpecs.includedNotIncludedDropdown,
    networkDependent: false,
  },
  commonCarrier: {
    mpOnly: true,
    label: "Common Carrier",
    mpConfigKey: "common_carrier",
    description: "AD&D common carrier benefit provision",
    cell: sharedCellSpecs.includedNotIncludedDropdown,
    networkDependent: false,
  },
  repatriation: {
    mpOnly: true,
    label: "Repatriation",
    mpConfigKey: "repatriation",
    description: "AD&D repatriation benefit provision",
    cell: sharedCellSpecs.includedNotIncludedDropdown,
    networkDependent: false,
  },
  seatBelt: {
    mpOnly: true,
    label: "Seat Belt",
    mpConfigKey: "seat_belt",
    description: "AD&D seat belt benefit provision",
    cell: sharedCellSpecs.includedNotIncludedDropdown,
    networkDependent: false,
  },
  annualOpenEnrollment: {
    label: "Annual Open Enrollment",
    mpConfigKey: "annual_open_enrollment",
    description: "Indicates if annual open enrollment is included in the plan",
    cell: sharedCellSpecs.annualOpenEnrollmentType,
    networkDependent: false,
    isRequired: true,
  },
  annualAbilityToIncreaseBenefit: {
    mpOnly: true,
    label: "Annual Ability to Increase Benefit",
    mpConfigKey: "annual_ability_to_increase_benefit",
    description:
      "Whether the employee can increase their benefit amount annually",
    cell: sharedCellSpecs.annualAbilityToIncreaseBenefitDropdown,
    networkDependent: false,
  },
  spouseAgeBasisEeOrSpAge: {
    mpOnly: true,
    label: "Spouse Age Basis - EE or SP age",
    mpConfigKey: "spouse_age_basis_ee_or_sp_age",
    description:
      "Whether spouse benefit reductions are based on the employee's or the spouse's age",
    cell: sharedCellSpecs.spouseAgeBasisDropdown,
    networkDependent: false,
  },
  // These sibling detail rows are collapsed into `ageReductionsEe`/
  // `ageReductionsSp` on the Quotes grid (SIBLING_COLLAPSE_BY_COVERAGE) and
  // never render as their own cell; the `text` cell here only exists to
  // satisfy the grid-definition registry (CD-2071/CD-2555).
  employeeAgeReductionSchedules: {
    mpOnly: true,
    label: "Age Reduction Schedules - EE",
    description:
      "Employee age reduction schedule details (array of { age, reductionPercent })",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  spouseAgeReductionSchedules: {
    mpOnly: true,
    label: "Age Reduction Schedules - SP",
    description:
      "Spouse age reduction schedule details (array of { age, reductionPercent })",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
  enrolledLives: mpOnlyRow("Enrolled Lives", "enrolled_lives"),
};
